import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, completeProfilePath, accountPath, storeAuthRedirect, peekAuthRedirect, storeReturnPathFromLocation } from '../utils/paths';
import PlayerRegistration from './PlayerRegistration';
import PlayerProfile from './PlayerProfile';
import PlayerList from './PlayerList';
import TeamCreation from './TeamCreation';
import TeamList from './TeamList';
import TeamView from './TeamView';
import TeamEdit from './TeamEdit';
import AdminPanel from './AdminPanel';
import SoccerAuthRedirect from './SoccerAuthRedirect';
import ResetPassword from './ResetPassword';
import ClubRegistration from './ClubRegistration';
import ClubHome from './ClubHome';
import ClubAdminDashboard from './ClubAdminDashboard';
import PlayerDetailProfile from './PlayerDetailProfile';
import ClubPlayerEdit from './ClubPlayerEdit';
import PlayerRegistrationLink from './PlayerRegistrationLink';
import MatchScheduling from './MatchScheduling';
import MatchPreviewEditor from './MatchPreviewEditor';
import PracticeScheduling from './PracticeScheduling';
import ClubCompletedPractices from './ClubCompletedPractices';
import AvailabilityVoting from './AvailabilityVoting';
import PracticeTeamFormation from './PracticeTeamFormation';
import PracticeLineupView from './PracticeLineupView';
import MyClubs from './MyClubs';
import ClubsList from './ClubsList';
import MatchSummaryForm from './MatchSummaryForm';
import PublicMatchSummary from './PublicMatchSummary';
import PublicMatchPreview from './PublicMatchPreview';
import PublicPracticeFormation from './PublicPracticeFormation';
import PublicFormationPlayerProfile from './PublicFormationPlayerProfile';
import PracticeScrimmageStats from './PracticeScrimmageStats';
import PublicPracticeScrimmageStats from './PublicPracticeScrimmageStats';
import SoccerLanding from './SoccerLanding';
import ClubBlogAdmin from './ClubBlogAdmin';
import ClubBlogEditor from './ClubBlogEditor';
import ClubTournamentAdmin from './ClubTournamentAdmin';
import ClubTournamentEditor from './ClubTournamentEditor';
import PublicClubTournament from './PublicClubTournament';
import PublicTrainingLog from './PublicTrainingLog';
import MembershipStatusBanner, { useMyClubMemberships } from './MembershipStatusBanner';
import AppSwitcher, { resolveActiveAppId } from './AppSwitcher';
import { SITE_NAME, SITE_APPS, formatAppTitle } from '../config/siteBranding';
import { setPageMeta, resetPageMeta } from '../utils/pageMeta';
import './MembershipStatusBanner.css';

const ProtectedRoute = ({ children, requireAdmin = false, allowUnauthenticated = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAuthenticated && !allowUnauthenticated) {
    storeAuthRedirect(window.location.pathname + window.location.search);
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin()) {
    return <Navigate to={soccerPath('/')} replace />;
  }

  return children;
};

const MatchSummaryFormWrapper = ({ type }) => {
  const { id } = useParams();
  return <MatchSummaryForm type={type} entityId={id} />;
};

function MatchAvailabilityPage() {
  const { id } = useParams();
  return <AvailabilityVoting type="match" entityId={id} />;
}

function PracticeAvailabilityPage() {
  const { id } = useParams();
  return <AvailabilityVoting type="practice" entityId={id} />;
}

const SoccerApp = () => {
  const { isAuthenticated, user, logout, isAdmin, isClubCreator } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasApprovedPlayer, setHasApprovedPlayer] = useState(false);
  const [hasPendingPlayer, setHasPendingPlayer] = useState(false);
  const location = useLocation();
  const { pendingMemberships } = useMyClubMemberships(isAuthenticated ? user?.id : null);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setHasApprovedPlayer(false);
      setHasPendingPlayer(false);
      return;
    }

    if (isAdmin() || isClubCreator()) {
      setHasApprovedPlayer(true);
      setHasPendingPlayer(false);
      return;
    }

    const checkPlayerStatus = async () => {
      try {
        const approvedPlayers = await api.getAllPlayers(user?.id);
        const approvedPlayer = approvedPlayers.find((p) => p.userId === user.id);
        setHasApprovedPlayer(!!approvedPlayer);

        if (!approvedPlayer) {
          try {
            const pendingPlayers = await api.getPendingPlayers();
            const pendingPlayer = pendingPlayers.find((p) => p.userId === user.id);
            setHasPendingPlayer(!!pendingPlayer);
          } catch {
            setHasPendingPlayer(false);
          }
        } else {
          setHasPendingPlayer(false);
        }
      } catch {
        setHasApprovedPlayer(false);
        setHasPendingPlayer(false);
      }
    };

    const timer = setTimeout(() => {
      checkPlayerStatus();
    }, 500);

    return () => clearTimeout(timer);
  }, [isAuthenticated, user, isAdmin, isClubCreator]);

  const urlParams = new URLSearchParams(location.search);
  const source = urlParams.get('source');
  const hasVotingRedirect = peekAuthRedirect();
  const isVotingPage =
    location.pathname.startsWith(soccerPath('/practices/')) ||
    location.pathname.startsWith(soccerPath('/matches/'));
  const isAuthPage =
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === soccerPath('/login') ||
    location.pathname === soccerPath('/register');
  const isPlayerRegistrationPage =
    location.pathname === soccerPath('/players/complete-profile')
    || location.pathname === soccerPath('/players/register');

  const hideNav =
    (source === 'whatsapp' && isVotingPage) ||
    (isAuthPage && hasVotingRedirect) ||
    (isPlayerRegistrationPage && hasVotingRedirect);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleAuthNavClick = () => {
    storeReturnPathFromLocation(location.pathname, location.search);
    closeMobileMenu();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        !event.target.closest('.nav-menu') &&
        !event.target.closest('.mobile-menu-toggle')
      ) {
        closeMobileMenu();
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const activeAppId = resolveActiveAppId(location.pathname);

  useEffect(() => {
    const app = activeAppId === 'training' ? SITE_APPS.training : SITE_APPS.soccer;
    setPageMeta({
      title: formatAppTitle(app.name),
      description: app.description,
      url: `${window.location.origin}${app.href}`,
    });
    return () => resetPageMeta();
  }, [activeAppId]);

  return (
    <>
      {!hideNav && (
        <nav className="navbar">
          <div className="nav-container">
            <div className="nav-brand-block">
              <Link to="/" className="nav-logo" onClick={closeMobileMenu}>
                {SITE_NAME}
              </Link>
              <AppSwitcher
                activeAppId={activeAppId}
                onNavigate={closeMobileMenu}
                variant="compact"
                className="nav-app-switcher"
              />
            </div>
            <button
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <div className={`nav-menu ${mobileMenuOpen ? 'nav-menu-open' : ''}`}>
              {!hasApprovedPlayer && !hasPendingPlayer && isAuthenticated && (
                <Link
                  to={completeProfilePath()}
                  className="nav-link"
                  onClick={closeMobileMenu}
                >
                  Complete player profile
                </Link>
              )}
              {hasPendingPlayer && (
                <span className="nav-link" style={{ color: '#856404', cursor: 'default' }}>
                  Player Registration Pending
                </span>
              )}
              {hasApprovedPlayer && (
                <Link
                  to={soccerPath('/players/profile')}
                  className="nav-link"
                  onClick={closeMobileMenu}
                >
                  My Profile
                </Link>
              )}
              {(hasApprovedPlayer || isAdmin() || isClubCreator()) && (
                <Link to={soccerPath('/players')} className="nav-link" onClick={closeMobileMenu}>
                  Players
                </Link>
              )}
              <Link to={soccerPath('/clubs/list')} className="nav-link" onClick={closeMobileMenu}>
                Clubs
              </Link>
              {isAuthenticated && (isAdmin() || isClubCreator()) && (
                <Link
                  to={soccerPath('/clubs/register')}
                  className="nav-link"
                  onClick={closeMobileMenu}
                >
                  Register Club
                </Link>
              )}
              {isAuthenticated && (isAdmin() || user?.roles?.includes('CLUB_ADMIN')) && (
                <Link to={soccerPath('/clubs')} className="nav-link" onClick={closeMobileMenu}>
                  My Clubs
                </Link>
              )}
              {isAdmin() && (
                <Link to={soccerPath('/admin')} className="nav-link" onClick={closeMobileMenu}>
                  Admin
                </Link>
              )}
              {isAuthenticated ? (
                <>
                  <Link to={accountPath()} className="nav-link" onClick={closeMobileMenu}>
                    Account
                  </Link>
                  <span className="nav-link nav-user-info" style={{ color: 'var(--color-brand)' }}>
                    {user?.name} (
                    {user?.roles && Array.isArray(user.roles) ? user.roles.join(', ') : 'N/A'})
                  </span>
                  <button
                    onClick={() => {
                      logout();
                      closeMobileMenu();
                    }}
                    className="btn btn-secondary nav-logout-btn"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="nav-link" onClick={handleAuthNavClick}>
                    Login
                  </Link>
                  <Link to="/register" className="nav-link" onClick={handleAuthNavClick}>
                    Join
                  </Link>
                </>
              )}
            </div>
          </div>
        </nav>
      )}

      <main className="main-content" style={hideNav ? { paddingTop: 0 } : {}}>
        {isAuthenticated && pendingMemberships.length > 0 && !hideNav && (
          <div className="container" style={{ paddingTop: '1rem' }}>
            <MembershipStatusBanner
              pendingMemberships={pendingMemberships}
              variant="compact"
              showPlayerPendingNote={hasPendingPlayer}
            />
          </div>
        )}
        <Routes>
          <Route index element={<SoccerLanding />} />
          <Route path="login" element={<SoccerAuthRedirect target="/login" />} />
          <Route path="register" element={<SoccerAuthRedirect target="/register" />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="players/complete-profile" element={<PlayerRegistration />} />
          <Route
            path="players/register"
            element={<Navigate to={soccerPath('/players/complete-profile')} replace />}
          />
          <Route path="players/profile" element={<PlayerProfile />} />
          <Route path="players" element={<PlayerList />} />
          <Route path="teams/create" element={<TeamCreation />} />
          <Route path="teams" element={<TeamList />} />
          <Route path="teams/:id" element={<TeamView />} />
          <Route
            path="teams/:id/edit"
            element={
              <ProtectedRoute>
                <TeamEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin"
            element={
              <ProtectedRoute requireAdmin={true}>
                <AdminPanel />
              </ProtectedRoute>
            }
          />
          <Route path="clubs/list" element={<ClubsList />} />
          <Route
            path="clubs"
            element={
              <ProtectedRoute>
                <MyClubs />
              </ProtectedRoute>
            }
          />
          <Route
            path="clubs/register"
            element={
              <ProtectedRoute>
                <ClubRegistration />
              </ProtectedRoute>
            }
          />
          <Route path="clubs/:id" element={<ClubHome />} />
          <Route
            path="clubs/:id/players/:playerId"
            element={
              <ProtectedRoute>
                <PlayerDetailProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="clubs/:id/players/:playerId/edit"
            element={
              <ProtectedRoute>
                <ClubPlayerEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="clubs/:id/admin"
            element={
              <ProtectedRoute>
                <ClubAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="register-player/:token" element={<PlayerRegistrationLink />} />
          <Route
            path="clubs/:id/matches"
            element={
              <ProtectedRoute>
                <MatchScheduling />
              </ProtectedRoute>
            }
          />
          <Route
            path="clubs/:id/matches/:matchId/preview"
            element={
              <ProtectedRoute>
                <MatchPreviewEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="clubs/:id/practices"
            element={
              <ProtectedRoute>
                <PracticeScheduling />
              </ProtectedRoute>
            }
          />
          <Route
            path="clubs/:id/practices/completed"
            element={
              <ProtectedRoute>
                <ClubCompletedPractices />
              </ProtectedRoute>
            }
          />
          <Route
            path="matches/:id"
            element={
              <ProtectedRoute allowUnauthenticated={true}>
                <MatchAvailabilityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="practices/:id"
            element={
              <ProtectedRoute allowUnauthenticated={true}>
                <PracticeAvailabilityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="practices/:id/teams"
            element={
              <ProtectedRoute>
                <PracticeTeamFormation />
              </ProtectedRoute>
            }
          />
          <Route
            path="practices/:id/lineup"
            element={
              <ProtectedRoute>
                <PracticeLineupView />
              </ProtectedRoute>
            }
          />
          <Route
            path="practices/:id/scrimmage-stats"
            element={
              <ProtectedRoute>
                <PracticeScrimmageStats />
              </ProtectedRoute>
            }
          />
          <Route
            path="matches/:id/summary"
            element={
              <ProtectedRoute>
                <MatchSummaryFormWrapper type="match" />
              </ProtectedRoute>
            }
          />
          <Route
            path="practices/:id/summary"
            element={
              <ProtectedRoute>
                <MatchSummaryFormWrapper type="practice" />
              </ProtectedRoute>
            }
          />
          <Route
            path="clubs/:id/admin/blog/new"
            element={
              <ProtectedRoute>
                <ClubBlogEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="clubs/:id/admin/blog/:postId/edit"
            element={
              <ProtectedRoute>
                <ClubBlogEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="clubs/:id/admin/blog"
            element={
              <ProtectedRoute>
                <ClubBlogAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="clubs/:id/admin/tournaments"
            element={
              <ProtectedRoute>
                <ClubTournamentAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="clubs/:id/admin/tournaments/:tournamentId"
            element={
              <ProtectedRoute>
                <ClubTournamentEditor />
              </ProtectedRoute>
            }
          />
          <Route path="training" element={<Navigate to="/training" replace />} />
          <Route path="training/*" element={<Navigate to="/training" replace />} />
          <Route path="public/summary/:token" element={<PublicMatchSummary />} />
          <Route path="public/match-preview/:token" element={<PublicMatchPreview />} />
          <Route path="public/practice-formation/:token" element={<PublicPracticeFormation />} />
          <Route
            path="public/practice-formation/:token/players/:playerId"
            element={<PublicFormationPlayerProfile />}
          />
          <Route path="public/scrimmage-stats/:token" element={<PublicPracticeScrimmageStats />} />
          <Route path="public/tournament/:token" element={<PublicClubTournament />} />
          <Route path="public/training/:token" element={<PublicTrainingLog />} />
        </Routes>
      </main>
    </>
  );
};

export default SoccerApp;
